import React, { useState } from "react";
import { X, Gift, Loader2, Check } from "lucide-react";
import { createGift } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function GiftModal({ section, onClose }) {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      await createGift({ section_id: section.id, recipient_email: email, note });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send the gift.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-obsidian-950/85 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="gift-modal"
    >
      <div className="relative bg-obsidian-800 border border-obsidian-600 max-w-lg w-full">
        <button
          onClick={onClose}
          data-testid="gift-modal-close"
          className="absolute top-4 right-4 text-bone-500 hover:text-bone-200 transition-colors z-10"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-9 w-9 border border-terracotta/50 flex items-center justify-center">
              <Gift className="h-4 w-4 text-terracotta" />
            </div>
            <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta">
              A gift, gently offered
            </p>
          </div>

          {sent ? (
            <div className="animate-fade-up">
              <div className="h-10 w-10 border border-terracotta text-terracotta flex items-center justify-center mb-6">
                <Check className="h-5 w-5" />
              </div>
              <h2 className="font-serif text-3xl text-bone-100 mb-3">The gift is on its way.</h2>
              <p className="font-sans text-sm text-bone-400 leading-relaxed mb-6">
                When <span className="text-bone-200">{email}</span> signs in with Google, they will
                find <em className="text-bone-200 italic">{section.title}</em> waiting in their inbox.
              </p>
              <Button
                onClick={onClose}
                data-testid="gift-done-btn"
                className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-6 py-5 transition-colors"
              >
                Return to the workbook
              </Button>
            </div>
          ) : (
            <form onSubmit={submit}>
              <h2 className="font-serif text-3xl text-bone-100 mb-3 leading-tight">
                Send <em className="text-bone-300 italic">{section.title}</em> to someone soft.
              </h2>
              <p className="font-sans text-sm text-bone-400 leading-relaxed mb-8">
                They'll receive this chapter on the house when they sign in with Google using the
                email below. No coupon codes, no forwarded links — it just appears.
              </p>

              <label className="block font-sans uppercase tracking-[0.2em] text-[0.65rem] text-bone-500 mb-3">
                Recipient's email
              </label>
              <Input
                data-testid="gift-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="friend@example.com"
                required
                className="bg-obsidian-900 border-obsidian-600 focus-visible:ring-1 focus-visible:ring-terracotta text-bone-100 font-sans rounded-none placeholder:text-bone-500 mb-6"
              />

              <label className="block font-sans uppercase tracking-[0.2em] text-[0.65rem] text-bone-500 mb-3">
                A note (optional)
              </label>
              <Textarea
                data-testid="gift-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Something small, just for them."
                rows={3}
                className="bg-obsidian-900 border-obsidian-600 focus-visible:ring-1 focus-visible:ring-terracotta text-bone-100 font-serif italic rounded-none placeholder:text-bone-500 mb-8"
              />

              <Button
                type="submit"
                disabled={busy}
                data-testid="gift-send-btn"
                className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-6 py-5 transition-colors w-full"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Gift className="h-4 w-4 mr-2" />}
                Send this chapter
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
